// help mini-css-extract-plugin to define styles order
import '../ActionsDropdown/ActionsDropdown.css'

import compose from 'lodash/flowRight'
import * as React from 'react'
import type {ReactNode} from 'react'
import type {ConnectedProps} from 'react-redux'
import {connect, shallowEqual} from 'react-redux'

import {toggleQueuedVisibility} from '../../../actions'
import {OneLineDetector} from '../../../containers/OneLineDetector'
import {withBuildLineLayout} from '../../../contexts/buildLineLayout'
import {withLocator} from '../../../contexts/locator'
import type {OneLineProps} from '../../../contexts/oneLine'
import {withOneLineContext} from '../../../contexts/oneLine'
import withHook from '../../../hocs/withHook'
import withStatusSubscription from '../../../hocs/withStatusSubscription'
import withWrapper from '../../../hocs/withWrapper'
import {
  useBooleanQueryParam,
  useQueryParam,
  withBranchProps,
  withBuildTypeId,
  withProjectId,
} from '../../../hooks/routes'
import type {State} from '../../../reducers/types'
import {getProjectsArg} from '../../../rest/projects'
import {
  getBuildsLocatorWithPage,
  getBuildsFilteredByPage,
  getBuildsReady,
  getIsSakuraUI,
  getStatus,
  makeGetMaybeBuilds,
  getBuildsFilteredBySearchQuery,
} from '../../../selectors'
import {restApi} from '../../../services/rest'
import {getBuildTypeStatusRequest, ROOT_PROJECT_ID} from '../../../types'
import {notNull} from '../../../utils/guards'
import stableSort from '../../../utils/stableSort'
import {submitPager} from '../Pager/Pager.actions.base'
import {usePage} from '../Pager/Pager.hooks'
import {useSequenceLoaderReadyHandler} from '../SequenceLoader/SequenceLoader.hooks'

import Builds from './Builds'
import type {BuildsRenderProps, ReduxOwnProps, StateProps, BuildsProps} from './Builds.types'

import styles from './Builds.css'

import {useAppDispatch, useAppSelector} from 'src/hooks/react-redux'

export type BuildsContainerProps = BuildsProps & BuildsRenderProps
function BuildsContainer({render, ...restProps}: BuildsContainerProps) {
  if (render) {
    const {builds, ...args} = restProps
    return (
      <>
        {render({
          builds,
          args,
        })}
      </>
    )
  } else {
    return <Builds {...restProps} />
  }
}

type EmptyWrapperProps = {
  children: ReactNode
}
function EmptyWrapper({children}: EmptyWrapperProps) {
  return <div className={styles.hasParentGrid}>{children}</div>
}

const mapStateToProps: () => (state: State, props: ReduxOwnProps) => StateProps = () => {
  const getBuilds = makeGetMaybeBuilds()
  return (
    state,
    {
      withPager,
      page = 1,
      showOverlayOnLoading,
      lastReadyLocator,
      locator,
      placeholder,
      buildLineLayout: {showPath} = {},
      buildTypeId,
      branch,
    },
  ) => {
    const locatorWithPage =
      locator && (withPager ? getBuildsLocatorWithPage(state, locator, page) : locator)
    const showOverlay =
      showOverlayOnLoading === true &&
      lastReadyLocator != null &&
      lastReadyLocator !== locatorWithPage
    const stats = buildTypeId
      ? getStatus(state, getBuildTypeStatusRequest(buildTypeId, branch))
      : null
    const dataLocator = showOverlay ? lastReadyLocator : locatorWithPage
    const builds = getBuilds(state, dataLocator)
    return {
      key: dataLocator,
      builds,
      showOverlay,
      ready: getBuildsReady(state, locatorWithPage, showPath),
      placeholder: placeholder ?? (withPager ? '' : undefined),
      stats,
      isSakuraUI: getIsSakuraUI(state),
    }
  }
}

const actionCreators = {
  toggleQueuedVisibility,
}

const connector = connect(mapStateToProps, actionCreators)

// compose is only well typed for 2-7 arguments
export default compose(
  compose(
    withBuildLineLayout,
    withLocator,
    withProjectId,
    withBuildTypeId,
    withBranchProps,
    withHook(() => ({page: usePage()})),
    connector,
    withOneLineContext, // Don't use one-line presentation with path
  ),
  compose(
    // Also, don't use detector if it's already present somewhere above
    withWrapper(
      ({
        buildLineLayout,
        oneLine,
      }: ReduxOwnProps & ConnectedProps<typeof connector> & OneLineProps) =>
        buildLineLayout?.showPath || oneLine !== undefined ? EmptyWrapper : OneLineDetector,
    ),
    withHook(props => {
      const {
        builds,
        sortBy,
        ready,
        withClientPager,
        buildLineLayout,
        buildTypeId,
        withCollapsedQueued,
        hasQueuedOnly,
        withClientFilter,
        stats,
        page = 1,
      } = props
      const tag = useQueryParam('tag')
      const useRawLocator = useBooleanQueryParam('use-raw-locator')
      const buildNamePattern = useQueryParam('build-name-pattern')
      const sortedBuilds = React.useMemo(
        () => (sortBy != null ? stableSort(builds.filter(notNull), sortBy) : builds),
        [builds, sortBy],
      )
      const buildsBySearchQuery = useAppSelector(state =>
        getBuildsFilteredBySearchQuery(state, sortedBuilds, buildNamePattern),
      )
      const filteredBuilds = useAppSelector(
        state =>
          withClientPager === true && !useRawLocator
            ? getBuildsFilteredByPage(state, buildsBySearchQuery, page)
            : buildsBySearchQuery,
        shallowEqual,
      )
      const mayShowQueuedToggler =
        buildTypeId != null &&
        withCollapsedQueued &&
        !hasQueuedOnly &&
        (useRawLocator || tag == null) &&
        (page === 1 ||
          (page > 1 && filteredBuilds.filter(build => build.state === 'queued').length > 0))
      const showQueuedToggler = mayShowQueuedToggler && Boolean(stats?.queued)
      const waitForQueuedToggler = mayShowQueuedToggler && stats == null
      const dispatch = useAppDispatch()
      const isClientFilter = Boolean(buildNamePattern)
      useSequenceLoaderReadyHandler(!waitForQueuedToggler && ready)

      restApi.endpoints.getAllProjectsNormalized.useQuerySubscription(
        getProjectsArg(ROOT_PROJECT_ID, {
          withBuildTypes: false,
          withLinks: true,
        }),
        {skip: !buildLineLayout?.showPath},
      )

      React.useEffect(() => {
        if (withClientPager && withClientFilter) {
          dispatch(
            submitPager({
              total: buildsBySearchQuery.length,
              isClientFilter,
            }),
          )
        }
      }, [buildsBySearchQuery.length, withClientFilter, dispatch, withClientPager, isClientFilter])

      return {
        builds: filteredBuilds,
        show: !waitForQueuedToggler && (ready || sortedBuilds.length > 0),
        disableProgressiveLoading: sortBy != null,
        showQueuedToggler,
      }
    }),
    withStatusSubscription(),
  ),
)(BuildsContainer)
